import React, { useEffect, useRef, useState } from "react";
import { getPermits, getPermit, getParameters, getSamples, submitSample, attachLabReport, getFlowReports, getSamplingSchedule, getCertificationContext } from "../../api/client";
import { Permit, Parameter, PermitLimit } from "../../types";
import COAImport from "../COAImport";
import { sampleSanityWarning } from "../../utils/sampleSanity";
import { useIsNarrow } from "../../hooks/useMediaQuery";
import { pushToast } from "../../ui/toast";

interface Props {
  companyId: number;
  companyName: string;
  // false for a company exempt from sewer surcharges: flow is for permit limits only.
  paysSurcharge?: boolean;
  onSubmitted: () => void;
  // When set (e.g. from a "due" item on the dashboard), the matching parameter
  // card is scrolled to, focused, and briefly highlighted; on a single-permit
  // company the permit is auto-selected so the cards are showing.
  highlightParam?: string | null;
}

type ResultStatus = "mr" | "pass" | "fail" | "range_ok" | "range_fail" | null;

// Concentration/range pass-fail for a typed result. Shared by the per-parameter
// card badges and the pre-submit recap so they can never disagree.
function concentrationStatus(limit: PermitLimit, val: number | null): ResultStatus {
  if (limit.is_monitor_report) return "mr";
  if (val === null || isNaN(val)) return null;
  if (limit.is_range_limit) {
    const lo = limit.min_value ?? -Infinity;
    const hi = limit.max_value ?? Infinity;
    return val >= lo && val <= hi ? "range_ok" : "range_fail";
  }
  const maxC = limit.daily_max_concentration;
  const minC = limit.daily_min_concentration;
  if (maxC != null && val > maxC) return "fail";
  if (minC != null && val < minC) return "fail";
  return "pass";
}

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

export default function SampleForm({ companyId, companyName, onSubmitted, highlightParam, paysSurcharge = true }: Props) {
  const draftKey = `draft_sample_${companyId}`;

  const [showCOA, setShowCOA]               = useState(false);
  // The imported lab report: kept with the sample when it is submitted.
  const [coaFile, setCoaFile]               = useState<File | null>(null);
  const [coaJobId, setCoaJobId]             = useState<string | null>(null);
  const [showImportPrompt, setShowImportPrompt] = useState(true);
  const [highlightedId, setHighlightedId]   = useState<number | null>(null);

  // Responsive: on phones/small tablets, stack the 3-column grids and tighten padding.
  const isNarrow  = useIsNarrow();
  const gridCols  = isNarrow ? { ...s.grid3, gridTemplateColumns: "1fr" } : s.grid3;
  const formStyle = isNarrow ? { ...s.form, padding: 16 } : s.form;
  const [permits, setPermits]               = useState<Permit[]>([]);
  const [parameters, setParameters]         = useState<Parameter[]>([]);
  const [permitId, setPermitId]             = useState<number | "">("");
  const [limits, setLimits]                 = useState<PermitLimit[]>([]);
  const [sampleDate, setSampleDate]         = useState("");
  const [samplerName, setSamplerName]       = useState("");
  const [samplerSuggestions, setSamplerSuggestions] = useState<string[]>([]);
  const [samplerDropdownOpen, setSamplerDropdownOpen] = useState(false);
  const samplerComboRef = useRef<HTMLDivElement>(null);
  const [temperature, setTemperature]       = useState("");
  const [sampleFlowMgd, setSampleFlowMgd]   = useState("");
  const [results, setResults]               = useState<Record<number, string>>({});
  const [nonDetect, setNonDetect]           = useState<Record<number, boolean>>({});
  const [reportedLimit, setReportedLimit]   = useState<Record<number, string>>({});
  const [submitting, setSubmitting]         = useState(false);
  const [message, setMessage]               = useState<{type:"success"|"error", text:string} | null>(null);
  const [overdueAlerts, setOverdueAlerts]   = useState<any[]>([]);
  const [flowReports, setFlowReports]       = useState<any[]>([]);
  const [monthlyFlowReport, setMonthlyFlowReport] = useState<any | null>(null);
  const [draftSavedAt, setDraftSavedAt]     = useState<Date | null>(null);
  const [draftRestored, setDraftRestored]   = useState(false);
  const draftMounted = useRef(false);
  const resultInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Certification (40 CFR 403.12(l)): a sample can only be submitted by the company's
  // designated signature authority, re-authenticated. Mirrors the batch flow.
  const [certCtx, setCertCtx]         = useState<{ statement_text: string; statement_version: string; can_sign: boolean; authority_name: string | null; authority_title?: string | null } | null>(null);
  const [certChecked, setCertChecked] = useState(false);
  const [certName, setCertName]       = useState("");
  const [certPassword, setCertPassword] = useState("");
  const certReady = !!certCtx?.can_sign && certChecked && certName.trim() !== "" && certPassword !== "";

  useEffect(() => {
    getPermits().then(r => setPermits(r.data.filter((p: Permit) => p.company_id === companyId))).catch(() => {});
    getParameters().then(r => setParameters(r.data)).catch(() => {});
    getSamples(companyId).then(r => {
      const names: string[] = Array.from(
        new Set(r.data.map((s: any) => s.sampler_name).filter(Boolean))
      );
      setSamplerSuggestions(names);
    }).catch(() => {});
    getFlowReports(companyId).then(r => setFlowReports(r.data)).catch(() => {});
    getCertificationContext(companyId).then(r => setCertCtx(r.data)).catch(() => {});

    // Restore saved draft if present
    const saved = (() => { try { return JSON.parse(localStorage.getItem(draftKey) || "null"); } catch { return null; } })();
    if (saved) {
      if (saved.permitId)    setPermitId(saved.permitId);
      if (saved.sampleDate)  setSampleDate(saved.sampleDate);
      if (saved.samplerName) setSamplerName(saved.samplerName);
      if (saved.temperature) setTemperature(saved.temperature);
      if (saved.results)     setResults(saved.results);
      if (saved.nonDetect)     setNonDetect(saved.nonDetect);
      if (saved.reportedLimit) setReportedLimit(saved.reportedLimit);
      setDraftRestored(true);
    }

    getSamplingSchedule(companyId).then(r => {
      setOverdueAlerts(r.data.filter((x: any) => x.status === "overdue" || x.status === "never"));
    }).catch(() => {});
  }, [companyId]);

  // Auto-save draft to localStorage whenever any field changes (skips initial mount)
  useEffect(() => {
    if (!draftMounted.current) { draftMounted.current = true; return; }
    const draft = { permitId, sampleDate, samplerName, temperature, results, nonDetect, reportedLimit };
    // Nothing typed yet (selecting the only permit doesn't count): no draft to keep,
    // and no "Draft saved" note on an empty form.
    const hasContent = !!(sampleDate || samplerName || temperature
      || Object.values(results ?? {}).some(v => v !== "" && v != null)
      || Object.values(nonDetect ?? {}).some(Boolean));
    if (!hasContent) {
      try { localStorage.removeItem(draftKey); } catch {}
      setDraftSavedAt(null);
      return;
    }
    try { localStorage.setItem(draftKey, JSON.stringify(draft)); } catch {}
    setDraftSavedAt(new Date());
  }, [permitId, sampleDate, samplerName, temperature, results, nonDetect, reportedLimit]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (samplerComboRef.current && !samplerComboRef.current.contains(e.target as Node))
        setSamplerDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!permitId) return;
    getPermit(permitId as number)
      .then(r => setLimits(r.data.limits ?? []))
      .catch(() => {
        setLimits([]);
        setPermitId("");
      });
  }, [permitId]);

  // Pre-select the company's one active permit (or its only permit) so the
  // parameter cards render without a pick; with several to choose from, leave it.
  useEffect(() => {
    if (permitId !== "") return;
    const active = permits.filter(p => (p as any).status ? (p as any).status === "active" : p.is_active);
    if (active.length === 1) setPermitId(active[0].id);
    else if (permits.length === 1) setPermitId(permits[0].id);
    // permitId intentionally omitted from deps: re-running on permitId change
    // would re-select after a manual clear. permits/highlightParam are enough.
  }, [highlightParam, permits]);

  // Scroll to, focus, and briefly highlight the deep-linked parameter card.
  useEffect(() => {
    if (!highlightParam) return;
    const visible = limits.filter(l => !l.is_flow_limit);
    const idx = visible.findIndex(
      l => (l.parameter_name ?? "").toLowerCase() === highlightParam.toLowerCase()
    );
    if (idx < 0) return;
    setHighlightedId(visible[idx].id);
    const el = resultInputRefs.current[idx];
    if (el) { el.scrollIntoView?.({ behavior: "smooth", block: "center" }); el.focus?.(); }
    const t = setTimeout(() => setHighlightedId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightParam, limits]);

  // Find the monthly flow report matching the selected sample date's period
  useEffect(() => {
    if (!sampleDate) { setMonthlyFlowReport(null); return; }
    const d     = new Date(sampleDate + "T00:00:00");
    const month = d.getMonth() + 1;
    const year  = d.getFullYear();
    const found = flowReports.find(
      (r: any) => r.report_month === month && r.report_year === year
    ) ?? null;
    setMonthlyFlowReport(found);
  }, [sampleDate, flowReports]);

  const reportingPeriod = (() => {
    if (!sampleDate) return null;
    const d     = new Date(sampleDate + "T00:00:00");
    const year  = d.getFullYear();
    const month = d.getMonth();
    const days  = new Date(year, month + 1, 0).getDate();
    const start = new Date(year, month, 1).toLocaleDateString("en-US", { month:"long", day:"numeric", year:"numeric" });
    const end   = new Date(year, month + 1, 0).toLocaleDateString("en-US", { month:"long", day:"numeric", year:"numeric" });
    return { days, label: `${start} – ${end}` };
  })();

  // Any lab result entered yet? Drives the "import from lab report" front-door prompt.
  const hasAnyResult = Object.values(results).some(v => v !== "");

  // Pre-submit recap totals: how many results entered, how many exceed a limit
  // (concentration/range, plus loading when flow is given). Mirrors the per-card
  // badges so the recap and cards always agree.
  const recap = (() => {
    const visible = limits.filter(l => !l.is_flow_limit);
    const flow = sampleFlowMgd !== "" ? parseFloat(sampleFlowMgd) : null;
    let entered = 0, exceedances = 0;
    for (const limit of visible) {
      const raw = results[limit.id] ?? "";
      if (raw !== "" || (nonDetect[limit.id] ?? false)) entered++;
      const v = raw !== "" ? parseFloat(raw) : null;
      const st = concentrationStatus(limit, v);
      if (st === "fail" || st === "range_fail") { exceedances++; continue; }
      if (v !== null && !isNaN(v) && flow !== null && flow > 0 &&
          !limit.is_range_limit && !limit.is_monitor_report &&
          limit.daily_max_loading != null && !limit.daily_max_loading_is_mr) {
        const p = parameters.find(pp => pp.id === limit.parameter_id);
        const cf = p?.conversion_factor ?? 8.34;
        if (v * flow * cf > limit.daily_max_loading) exceedances++;
      }
    }
    return { entered, exceedances };
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sampleDate) {
      setMessage({ type: "error", text: "Please enter a sample date." });
      return;
    }
    // A non-detect means "below the lab's reporting limit", so that limit is required.
    const ndMissing = limits.filter(l => (nonDetect[l.id] ?? false) && !(parseFloat(reportedLimit[l.id] ?? "") > 0));
    if (ndMissing.length > 0) {
      setMessage({ type: "error", text: `Enter the lab's reporting limit for each non-detect result: ${ndMissing.map(l => l.parameter_name).join(", ")}.` });
      return;
    }
    if (!certReady) {
      setMessage({ type: "error", text: "This submission must be certified — complete the certification at the bottom before submitting." });
      return;
    }
    setSubmitting(true); setMessage(null);
    try {
      const payload: any = {
        company_id:   companyId,
        permit_id:    permitId,
        sample_date:  sampleDate,
        sampler_name: samplerName,
        temperature:  parseFloat(temperature) || null,
        flow_mgd:     sampleFlowMgd !== "" ? parseFloat(sampleFlowMgd) : null,
        signer_name:  certName.trim(),
        password:     certPassword,
        // Include any row that has a concentration OR is flagged non-detect.
        results: (() => {
          const ids = new Set<number>();
          Object.entries(results).forEach(([id, v]) => { if (v !== "") ids.add(parseInt(id)); });
          Object.entries(nonDetect).forEach(([id, v]) => { if (v) ids.add(parseInt(id)); });
          return Array.from(ids).map(id => ({
            permit_limit_id: id,
            concentration:   results[id] && results[id] !== "" ? parseFloat(results[id]) : null,
            non_detect:      nonDetect[id] ?? false,
            reported_limit_mg_l: reportedLimit[id] && reportedLimit[id] !== "" ? parseFloat(reportedLimit[id]) : null,
          }));
        })(),
        lab_job_id: coaFile ? coaJobId : null,
      };
      const res = await submitSample(payload);
      const { violations } = res.data;
      // Keep the lab report the results came from. The sample is already saved, so
      // a failed upload only warns — the report can be added later by the coordinator.
      let reportNote = "";
      if (coaFile && res.data.sample?.id) {
        // Also as a notice: the dashboard moves to the Samples tab after a submit.
        try {
          await attachLabReport(res.data.sample.id, coaFile);
          reportNote = ` Lab report ${coaFile.name} saved with the sample.`;
          pushToast(`Lab report ${coaFile.name} saved with the sample.`, "success");
        } catch {
          reportNote = ` The lab report ${coaFile.name} could not be saved — ask your coordinator to add it.`;
          pushToast(`The sample was submitted, but the lab report ${coaFile.name} could not be saved — ask your coordinator to add it.`, "error", 12000);
        }
      }
      setMessage({
        type: violations.length > 0 ? "error" : "success",
        text: (violations.length > 0
          ? `Submitted — ${violations.length} violation(s) detected. Enforcement notices generated.`
          : "Submitted — no violations detected. ✓") + reportNote,
      });
      setCoaFile(null);
      setCoaJobId(null);
      try { localStorage.removeItem(draftKey); } catch {}
      setDraftSavedAt(null);
      setDraftRestored(false);
      setResults({});
      setNonDetect({});
      setReportedLimit({});
      setCertChecked(false);
      setCertPassword("");   // never retain the password after use
      onSubmitted();
    } catch (err: any) {
      setMessage({ type: "error", text: err?.response?.data?.error ?? "Submission failed. Please check your entries." });
    } finally {
      setSubmitting(false);
    }
  };

  if (showCOA) {
    return (
      <div style={{...formStyle, maxWidth:900}}>
        <h2 style={s.title}>Import from COA PDF</h2>
        <COAImport
          companyId={companyId}
          companyName={companyName}
          parameters={parameters}
          onCancel={() => setShowCOA(false)}
          onConfirm={payload => {
            if (payload.permit_id) setPermitId(payload.permit_id);
            if (payload.sample_date) setSampleDate(payload.sample_date);
            const prefilled: Record<number, string> = {};
            const prefilledND: Record<number, boolean> = {};
            const prefilledRL: Record<number, string> = {};
            for (const r of payload.results) {
              if (!r.permit_limit_id) continue;
              if (r.non_detect) {
                prefilledND[r.permit_limit_id] = true;
                if (r.reported_limit_mg_l != null) prefilledRL[r.permit_limit_id] = String(r.reported_limit_mg_l);
              } else if (r.concentration !== null) {
                prefilled[r.permit_limit_id] = String(r.concentration);
              }
            }
            setResults(prefilled);
            setNonDetect(prefilledND);
            setReportedLimit(prefilledRL);
            setCoaFile(payload._file ?? null);
            setCoaJobId(payload.coa_job_id ?? null);
            setShowCOA(false);
          }}
        />
      </div>
    );
  }

  const discardDraft = () => {
    try { localStorage.removeItem(draftKey); } catch {}
    setPermitId(""); setSampleDate(""); setSamplerName(""); setTemperature("");
    setResults({}); setNonDetect({}); setReportedLimit({});
    setCoaFile(null); setCoaJobId(null);
    setDraftSavedAt(null); setDraftRestored(false);
  };

  return (
    <form onSubmit={handleSubmit} style={formStyle}>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4}}>
        <h2 style={{...s.title, marginBottom:0}}>Submit Sample Data</h2>
        <div style={{display:"flex", alignItems:"center", gap:8}}>
          {draftSavedAt && (
            <span style={{fontSize:11, color:"#718096"}}>
              Draft saved {draftSavedAt.toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}
            </span>
          )}
          <button type="button"
            style={{padding:"7px 16px", background:"#e9d8fd", color:"#553c9a",
                    border:"1px solid #b794f4", borderRadius:5, fontWeight:600,
                    cursor:"pointer", fontSize:13}}
            onClick={() => setShowCOA(true)}>
            📄 Import from COA PDF
          </button>
        </div>
      </div>

      {coaFile && (
        <div style={s.coaKept}>
          <span>📎 Lab report <strong>{coaFile.name}</strong>{coaJobId ? ` (job ${coaJobId})` : ""} will be
            saved with this sample when you submit.</span>
          <button type="button" style={s.importDismiss}
            onClick={() => { setCoaFile(null); setCoaJobId(null); }}>don't keep it</button>
        </div>
      )}

      <p style={s.helpLine}>
        To submit: choose your <strong>permit</strong> and <strong>sample date</strong>, enter your lab{" "}
        <strong>results</strong> (or import a COA), then certify. Only the designated signature authority can certify &amp; submit.
      </p>

      {/* Front door: importing from a lab report (COA) is the primary, lowest-error
          path to getting results in. Shown until there's data entered or it's
          dismissed in favor of manual entry. */}
      {!hasAnyResult && showImportPrompt && (
        <div style={s.importPrompt}>
          <div style={{display:"flex", alignItems:"center", gap:12}}>
            <span style={{fontSize:24, lineHeight:1}} aria-hidden="true">📄</span>
            <div>
              <div style={{fontWeight:700, color:"#553c9a", fontSize:14}}>Have a lab report (COA)?</div>
              <div style={{fontSize:12.5, color:"#6b46c1", marginTop:2}}>
                Import the PDF and we'll fill in your results automatically — no hand-keying, far fewer typos.
              </div>
            </div>
          </div>
          <div style={{display:"flex", alignItems:"center", gap:14, flexWrap:"wrap"}}>
            <button type="button" style={s.importPrimaryBtn} onClick={() => setShowCOA(true)}>
              Start from lab report (PDF)
            </button>
            <button type="button" style={s.importDismiss} onClick={() => setShowImportPrompt(false)}>
              or enter results manually
            </button>
          </div>
        </div>
      )}

      {/* Draft restored banner */}
      {draftRestored && (
        <div style={{background:"#ebf8ff", border:"1px solid #90cdf4", borderRadius:6,
                     padding:"8px 14px", marginBottom:12, display:"flex",
                     alignItems:"center", justifyContent:"space-between"}}>
          <span style={{fontSize:12, color:"#2b6cb0"}}>
            Draft restored — your previous entries have been reloaded.
          </span>
          <button type="button" onClick={discardDraft}
            style={{fontSize:11, color:"#c53030", background:"none", border:"none",
                    cursor:"pointer", textDecoration:"underline", padding:0}}>
            Discard draft
          </button>
        </div>
      )}

      {/* Overdue sampling warning */}
      {overdueAlerts.length > 0 && (
        <div style={{background:"#fff5f5", border:"1px solid #feb2b2", borderRadius:6,
                     padding:"10px 14px", marginBottom:16}}>
          <strong style={{color:"#c53030", fontSize:13}}>
            ⚠ Overdue sampling requirements for this permit:
          </strong>
          <ul style={{margin:"6px 0 0 18px", padding:0, fontSize:12, color:"#742a2a"}}>
            {overdueAlerts.map((a: any, i: number) => (
              <li key={i}>
                <strong>{a.parameter_name}</strong>
                {a.status === "never"
                  ? " — never sampled"
                  : ` — ${a.days_overdue}d overdue (due ${a.next_due_date})`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Section 1: Permit & Period */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Permit Information</div>
        <div style={gridCols}>
          <div style={{...s.fieldGroup, gridColumn:"1 / 2"}}>
            <label style={s.label}>Permit</label>
            <select style={s.input} value={permitId}
              onChange={e => setPermitId(parseInt(e.target.value))} required>
              <option value="">Select permit…</option>
              {permits.map(p => (
                <option key={p.id} value={p.id}>
                  {companyName || `Company #${p.company_id}`} — {p.permit_number}
                  {/* A renewal keeps its number, so tell same-numbered permits apart by term. */}
                  {permits.some(o => o.id !== p.id && o.permit_number === p.permit_number)
                    && ` (${p.effective_date} – ${p.expiration_date})`}
                </option>
              ))}
            </select>
          </div>

          <div style={s.fieldGroup}>
            <label style={s.label}>Sample Date</label>
            <input style={s.input} type="date" value={sampleDate}
              onChange={e => setSampleDate(e.target.value)} required />
          </div>

          <div style={s.fieldGroup}>
            <label style={s.label}>Reporting Period</label>
            <div style={reportingPeriod ? s.computed : s.computedEmpty}>
              {reportingPeriod ? `${reportingPeriod.label} (${reportingPeriod.days} days)` : "Enter sample date"}
            </div>
          </div>
        </div>

        {/* Plant flow report status — informational only, separate from sample loading */}
        {sampleDate && (
          monthlyFlowReport ? (
            monthlyFlowReport.review_status === "reviewed" ? (
              <div style={s.flowReportBox}>
                <strong>Plant Flow Report — {MONTHS[monthlyFlowReport.report_month - 1]} {monthlyFlowReport.report_year}:</strong>{" "}
                Monthly Avg {monthlyFlowReport.monthly_avg_mgd?.toFixed(4)} MGD
                {monthlyFlowReport.daily_max_mgd != null && ` · Daily Max ${monthlyFlowReport.daily_max_mgd.toFixed(4)} MGD`}
                {" "}· <em>Reviewed</em> — used for plant flow permit limits{paysSurcharge && <> &amp; surcharge</>}.
              </div>
            ) : (
              <div style={{...s.flowReportWarn, borderColor:"#f6ad55", background:"#fffaf0", color:"#744210"}}>
                <strong>Plant Flow Report for {MONTHS[monthlyFlowReport.report_month - 1]} {monthlyFlowReport.report_year} is pending review.</strong>{" "}
                Monthly Avg: {monthlyFlowReport.monthly_avg_mgd?.toFixed(4)} MGD — used for plant flow limits{paysSurcharge && <> &amp; surcharge</>} (separate from sample loading).
              </div>
            )
          ) : (
            <div style={s.flowReportWarn}>
              <strong>No Plant Flow Report submitted</strong> for this period.
              Remember to submit your monthly plant flow report separately — it is used for flow permit limits{paysSurcharge ? " and surcharge calculations" : ""}.
            </div>
          )
        )}
      </div>

      {/* Section 2: Sample Collection */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Sample Collection</div>
        <div style={gridCols}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Sampler Name</label>
            <div ref={samplerComboRef} style={s.comboWrap}>
              <input style={{...s.input, width:"100%"}} value={samplerName}
                placeholder="Type or select…"
                onChange={e => { setSamplerName(e.target.value); setSamplerDropdownOpen(true); }}
                onFocus={() => setSamplerDropdownOpen(true)} />
              {samplerDropdownOpen && (() => {
                const filtered = samplerSuggestions.filter(n =>
                  n.toLowerCase().includes(samplerName.toLowerCase())
                );
                return filtered.length > 0 ? (
                  <div style={s.comboList}>
                    {filtered.map(name => (
                      <div key={name} style={s.comboOption}
                        onMouseDown={() => { setSamplerName(name); setSamplerDropdownOpen(false); }}>
                        {name}
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>
          </div>

          <div style={s.fieldGroup}>
            <label style={s.label}>Temperature (°C)</label>
            <input style={s.input} type="number" step="0.1" value={temperature}
              placeholder="Optional"
              onChange={e => setTemperature(e.target.value)} />
          </div>

          <div style={s.fieldGroup}>
            <label style={s.label}>
              Sample Flow (MGD)
              <span style={{fontWeight:400, color:"#718096", marginLeft:4, fontSize:11}}>
                — flow during this sampling event
              </span>
            </label>
            <input style={s.input} type="number" step="any" min="0"
              placeholder="e.g. 0.44"
              value={sampleFlowMgd}
              onChange={e => setSampleFlowMgd(e.target.value)} />
            {sampleFlowMgd !== "" && !isNaN(parseFloat(sampleFlowMgd)) && (
              <span style={{fontSize:11, color:"#718096", marginTop:3}}>
                Used to calculate pollutant loading (lbs/day)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Section 3: Lab Results — flow limits are checked via monthly flow report, not entered here */}
      {permitId !== "" && limits.filter(l => !l.is_flow_limit).length === 0 && limits.length === 0 && (
        <div style={{background:"#fffbeb", border:"1px solid #f6e05e", borderRadius:6,
                     padding:"10px 14px", marginBottom:16, fontSize:12, color:"#744210"}}>
          No parameters are configured for this permit yet. An administrator must add parameters &amp; limits before results can be entered.
        </div>
      )}
      {limits.filter(l => !l.is_flow_limit).length > 0 && (
        <div style={s.section}>
          <div style={s.sectionLabel}>Lab Results</div>
          <div style={gridCols}>
            {limits.filter(l => !l.is_flow_limit).map((limit, i) => {
              const raw = results[limit.id] ?? "";
              const val = raw !== "" ? parseFloat(raw) : null;
              const sanity = sampleSanityWarning(limit, val);

              const status = concentrationStatus(limit, val);

              let limitHintText: React.ReactNode = null;
              if (limit.is_monitor_report) {
                limitHintText = <span style={s.mrHint}>Monitor Report — no numeric limit</span>;
              } else if (limit.is_range_limit) {
                limitHintText = <span style={s.limitHint}>{limit.min_value}–{limit.max_value} {limit.range_unit}{limit.daily_max_loading != null ? ` / ${limit.daily_max_loading} lbs/d max` : ""}</span>;
              } else if (limit.daily_min_concentration != null || limit.daily_max_concentration != null || limit.weekly_max_concentration != null || limit.monthly_avg_concentration != null) {
                const parts: string[] = [];
                if (limit.daily_min_concentration != null) parts.push(`Min ${limit.daily_min_concentration} mg/L`);
                if (limit.daily_max_concentration != null) parts.push(`Max ${limit.daily_max_concentration} mg/L`);
                if (limit.weekly_max_concentration != null && !limit.weekly_max_concentration_is_mr) parts.push(`Wkly max ${limit.weekly_max_concentration} mg/L`);
                if (limit.monthly_avg_concentration != null && !limit.monthly_avg_concentration_is_mr) parts.push(`Mo. avg ${limit.monthly_avg_concentration} mg/L`);
                limitHintText = <span style={s.limitHint}>{parts.join(" / ")}</span>;
              } else if (limit.daily_max_loading) {
                limitHintText = <span style={s.limitHint}>Loading limit {limit.daily_max_loading} lbs/day</span>;
              }

              // Loading calculation: concentration (mg/L) × flow (MGD) × conversion factor
              const flowVal = sampleFlowMgd !== "" ? parseFloat(sampleFlowMgd) : null;
              const param   = parameters.find(p => p.id === limit.parameter_id);
              const cf      = param?.conversion_factor ?? 8.34;
              const loading = (
                val !== null && !isNaN(val) &&
                flowVal !== null && !isNaN(flowVal) && flowVal > 0 &&
                !limit.is_range_limit && !limit.is_monitor_report
              ) ? val * flowVal * cf : null;

              return (
                <div key={limit.id} style={highlightedId === limit.id ? {...s.paramCard, ...s.paramCardHighlight} : s.paramCard}>
                  <div style={s.paramNameRow}>
                    <span style={s.paramName}>{limit.parameter_name}</span>
                    {limit.sample_type && (
                      <span style={
                        /composite/i.test(limit.sample_type) ? s.badgeComposite
                        : /grab/i.test(limit.sample_type)    ? s.badgeGrab
                        : s.badgeSampleType
                      }>
                        {limit.sample_type}
                      </span>
                    )}
                  </div>
                  {limitHintText && <div style={s.paramHint}>{limitHintText}</div>}
                  <div style={s.paramInputRow}>
                    <input
                      style={{...s.input, flex:1, minWidth:0}}
                      type="number"
                      step="any"
                      placeholder="mg/L"
                      value={raw}
                      ref={el => { resultInputRefs.current[i] = el; }}
                      onChange={e => setResults(prev => ({...prev, [limit.id]: e.target.value}))}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const next = resultInputRefs.current[i + 1];
                          if (next) next.focus();
                        }
                      }}
                    />
                    {status === "mr"         && <span style={s.badgeMR}>MR</span>}
                    {status === "pass"        && <span style={s.badgePass}>✓ Pass</span>}
                    {status === "fail"        && <span style={s.badgeFail}>✗ Exceeds</span>}
                    {status === "range_ok"   && <span style={s.badgePass}>✓ In Range</span>}
                    {status === "range_fail" && <span style={s.badgeFail}>✗ Out of Range</span>}
                  </div>
                  {sanity && <div style={s.sanityWarn}>⚠ {sanity}</div>}
                  {/* Offered on monitor-and-report parameters too: a lab can report "<" for those. */}
                  {!limit.is_range_limit && (
                    <div style={{display:"flex", alignItems:"center", gap:12, marginTop:6, flexWrap:"wrap"}}>
                      <label style={{display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#4a5568", cursor:"pointer"}}>
                        <input type="checkbox" checked={nonDetect[limit.id] ?? false}
                          aria-label={`${limit.parameter_name} non-detect`}
                          onChange={e => setNonDetect(prev => ({...prev, [limit.id]: e.target.checked}))} />
                        Non-detect (reported &lt; limit)
                      </label>
                      {(nonDetect[limit.id] ?? false) && (
                        <span style={{display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#4a5568"}}>
                          Reporting limit <span style={{color:"#c53030"}}>*</span>
                          <input style={{...s.input, width:110, padding:"4px 8px",
                                         ...(!(parseFloat(reportedLimit[limit.id] ?? "") > 0) ? {borderColor:"#fc8181"} : {})}}
                            type="number" step="any" min="0" required
                            aria-label={`${limit.parameter_name} reporting limit`}
                            placeholder="mg/L" value={reportedLimit[limit.id] ?? ""}
                            onChange={e => setReportedLimit(prev => ({...prev, [limit.id]: e.target.value}))} />
                          {limit.required_reporting_level_mg_l != null && (
                            <span style={{fontSize:12, color:"#718096"}}>req. ≤ {limit.required_reporting_level_mg_l} mg/L</span>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                  {loading !== null && (
                    <div style={s.loadingRow}>
                      <span style={s.loadingLabel}>Loading:</span>
                      <span style={s.loadingValue}>{loading.toFixed(2)} lbs/day</span>
                      {limit.daily_max_loading != null && !limit.daily_max_loading_is_mr && (
                        loading <= limit.daily_max_loading
                          ? <span style={s.badgePass}>✓ Pass</span>
                          : <span style={s.badgeFail}>✗ Exceeds</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {message && (
        <div style={{...s.message,
          background: message.type === "success" ? "#c6f6d5" : "#fed7d7",
          color:      message.type === "success" ? "#276749" : "#c53030"}}>
          {message.text}
        </div>
      )}

      {/* ── Certification (40 CFR 403.12(l)) — required to submit ─────────── */}
      {permitId !== "" && (
        <div style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:16, margin:"20px 0", background:"#fbfaff" }}>
          <div style={{ fontWeight:700, color:"#553c9a", marginBottom:8 }}>Certification — required</div>
          <div style={{ fontSize:12.5, color:"#4a5568", lineHeight:1.5, marginBottom:12, fontStyle:"italic" }}>
            {certCtx?.statement_text ??
              "I certify under penalty of law that this document and all attachments were prepared under my direction or supervision…"}
          </div>
          {certCtx && !certCtx.can_sign ? (
            <div style={{ background:"#fffaf0", border:"1px solid #fbd38d", borderRadius:6, padding:"10px 12px", fontSize:13, color:"#7b341e" }}>
              Only this company's designated signature authority
              {certCtx.authority_name ? <> (<strong>{certCtx.authority_name}{certCtx.authority_title ? `, ${certCtx.authority_title}` : ""}</strong>)</> : null}
              {" "}may certify and submit samples. You are signed in without that authority — contact your coordinator if this should be you.
            </div>
          ) : (
            <>
              <label style={{ display:"flex", alignItems:"flex-start", fontSize:13, color:"#2d3748", marginBottom:10 }}>
                <input type="checkbox" checked={certChecked}
                  onChange={e => setCertChecked(e.target.checked)} style={{ marginRight:8, marginTop:3 }} />
                I am the authorized representative and I certify the above statement for this submission.
              </label>
              <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                <div style={{ flex:"1 1 220px" }}>
                  <label style={s.label}>Full legal name (electronic signature)</label>
                  <input style={s.input} value={certName} placeholder="Type your full name"
                    onChange={e => setCertName(e.target.value)} />
                </div>
                <div style={{ flex:"1 1 220px" }}>
                  <label style={s.label}>Account password (re-authenticate)</label>
                  <input style={s.input} type="password" value={certPassword} autoComplete="off"
                    placeholder="Re-enter your password" onChange={e => setCertPassword(e.target.value)} />
                </div>
              </div>
              <div style={{ fontSize:11.5, color:"#718096", marginTop:8 }}>
                Signing records your name, the exact statement above, a timestamp, and your IP address to the audit log
                ({certCtx?.statement_version ?? "—"}).
              </div>
            </>
          )}
        </div>
      )}

      {recap.entered > 0 && (
        <div style={s.recap}>
          <strong>Ready to submit:</strong>{" "}
          {recap.entered} result{recap.entered !== 1 ? "s" : ""} entered ·{" "}
          {recap.exceedances > 0
            ? <span style={{color:"#c53030", fontWeight:700}}>{recap.exceedances} exceedance{recap.exceedances !== 1 ? "s" : ""} flagged</span>
            : <span style={{color:"#276749", fontWeight:700}}>no exceedances</span>}
          {certName.trim() !== "" && <> · signing as <strong>{certName.trim()}{certCtx?.authority_title ? `, ${certCtx.authority_title}` : ""}</strong></>}
        </div>
      )}

      <div style={s.footer}>
        <button style={{ ...s.btn, ...(certReady ? {} : { opacity:0.5, cursor:"not-allowed" }) }}
          type="submit" disabled={submitting || !certReady}>
          {submitting ? "Submitting…" : "Certify & Submit Sample"}
        </button>
        <button type="button" disabled={submitting}
          style={{padding:"10px 20px", background:"#276749", color:"#fff", border:"none",
                  borderRadius:6, fontWeight:600, fontSize:14, cursor:"pointer"}}
          onClick={() => {
            const draft = { permitId, sampleDate, samplerName, temperature, results, nonDetect, reportedLimit };
            try { localStorage.setItem(draftKey, JSON.stringify(draft)); } catch {}
            setDraftSavedAt(new Date());
            setMessage({ type: "success", text: "Draft saved — you can return to finish this entry." });
          }}>
          Save Draft
        </button>
        <button type="button" disabled={submitting}
          style={{padding:"10px 20px", background:"#fff", color:"#c53030",
                  border:"1px solid #feb2b2", borderRadius:6, fontWeight:600,
                  fontSize:14, cursor:"pointer"}}
          onClick={() => {
            if (window.confirm("Discard this entry? All unsaved data will be cleared.")) {
              discardDraft();
              setMessage(null);
            }
          }}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const INPUT_HEIGHT = 38;

const s: Record<string, React.CSSProperties> = {
  form:           { background:"#fff", borderRadius:10, padding:28,
                    boxShadow:"0 2px 8px rgba(0,0,0,0.10)", maxWidth:900 },
  title:          { fontSize:20, fontWeight:700, color:"#1a365d", marginBottom:24,
                    paddingBottom:12, borderBottom:"2px solid #e2e8f0" },
  section:        { marginBottom:24 },
  sectionLabel:   { fontSize:11, fontWeight:700, color:"#718096", textTransform:"uppercase" as const,
                    letterSpacing:"0.08em", marginBottom:10, paddingBottom:4,
                    borderBottom:"1px solid #edf2f7" },
  grid3:          { display:"grid", gridTemplateColumns:"repeat(3, minmax(0, 1fr))", gap:"12px 16px" },
  paramCard:      { display:"flex", flexDirection:"column" as const, gap:4,
                    background:"#f7fafc", border:"1px solid #e2e8f0", borderRadius:8,
                    padding:"10px 12px" },
  paramName:      { fontSize:13, fontWeight:700, color:"#1a202c" },
  paramHint:      { fontSize:11, color:"#718096", marginBottom:2 },
  paramInputRow:  { display:"flex", alignItems:"center", gap:8 },
  badgePass:      { fontSize:11, fontWeight:700, color:"#276749", background:"#c6f6d5",
                    border:"1px solid #9ae6b4", borderRadius:4, padding:"2px 7px",
                    whiteSpace:"nowrap" as const },
  badgeFail:      { fontSize:11, fontWeight:700, color:"#c53030", background:"#fff5f5",
                    border:"1px solid #feb2b2", borderRadius:4, padding:"2px 7px",
                    whiteSpace:"nowrap" as const },
  badgeMR:        { fontSize:11, fontWeight:700, color:"#c05621", background:"#fffaf0",
                    border:"1px solid #f6ad55", borderRadius:4, padding:"2px 7px",
                    whiteSpace:"nowrap" as const },
  fieldGroup:     { display:"flex", flexDirection:"column" as const },
  label:          { fontSize:12, fontWeight:600, color:"#4a5568", marginBottom:5 },
  input:          { height:INPUT_HEIGHT, padding:"0 10px", border:"1px solid #cbd5e0",
                    borderRadius:6, fontSize:14, boxSizing:"border-box" as const,
                    background:"#fff", color:"#2d3748", outline:"none" },
  computed:       { height:INPUT_HEIGHT, padding:"0 10px", display:"flex", alignItems:"center",
                    background:"#ebf8ff", border:"1px solid #bee3f8",
                    borderRadius:6, fontSize:14, color:"#2b6cb0", fontWeight:600,
                    boxSizing:"border-box" as const },
  computedEmpty:  { height:INPUT_HEIGHT, padding:"0 10px", display:"flex", alignItems:"center",
                    background:"#f7fafc", border:"1px dashed #cbd5e0",
                    borderRadius:6, fontSize:13, color:"#a0aec0", fontStyle:"italic",
                    boxSizing:"border-box" as const },
  limitHint:      { fontWeight:400, color:"#a0aec0" },
  mrHint:         { fontWeight:600, fontSize:10, color:"#c05621", background:"#fffaf0",
                    border:"1px solid #f6ad55", borderRadius:4,
                    padding:"0 4px", marginLeft:4, verticalAlign:"middle" },
  mrBadge:        { height:INPUT_HEIGHT, padding:"0 10px", display:"flex", alignItems:"center",
                    background:"#fffaf0", border:"1px solid #f6ad55",
                    borderRadius:6, fontSize:13, color:"#c05621", fontWeight:600,
                    boxSizing:"border-box" as const },
  flowReportBox:  { fontSize:12, color:"#276749", background:"#f0fff4", border:"1px solid #9ae6b4",
                    borderRadius:5, padding:"7px 12px", marginTop:10 },
  flowReportWarn: { fontSize:12, color:"#744210", background:"#fffbeb", border:"1px solid #f6e05e",
                    borderRadius:5, padding:"7px 12px", marginTop:10 },
  comboWrap:      { position:"relative" as const },
  comboList:      { position:"absolute" as const, top:"100%", left:0, right:0, zIndex:100,
                    background:"#fff", border:"1px solid #cbd5e0", borderRadius:6,
                    boxShadow:"0 4px 12px rgba(0,0,0,0.12)", maxHeight:160,
                    overflowY:"auto" as const, marginTop:2 },
  comboOption:    { padding:"9px 12px", cursor:"pointer", fontSize:14,
                    borderBottom:"1px solid #edf2f7" },
  message:        { padding:"12px 16px", borderRadius:6, marginBottom:16,
                    fontSize:14, fontWeight:500 },
  importPrompt:   { display:"flex", alignItems:"center", justifyContent:"space-between",
                    gap:16, flexWrap:"wrap" as const, background:"#faf5ff",
                    border:"1px solid #d6bcfa", borderRadius:8, padding:"14px 18px",
                    marginBottom:16 },
  coaKept:        { display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
                    flexWrap:"wrap" as const, background:"#f0fff4", border:"1px solid #9ae6b4",
                    borderRadius:6, padding:"8px 12px", fontSize:13, color:"#276749", marginBottom:10 },
  importPrimaryBtn:{ padding:"9px 18px", background:"#553c9a", color:"#fff", border:"none",
                    borderRadius:6, fontWeight:700, fontSize:13.5, cursor:"pointer",
                    whiteSpace:"nowrap" as const },
  importDismiss:  { background:"none", border:"none", color:"#718096", fontSize:12.5,
                    cursor:"pointer", textDecoration:"underline", padding:0,
                    whiteSpace:"nowrap" as const },
  sanityWarn:     { fontSize:11.5, color:"#975a16", background:"#fffbeb",
                    border:"1px solid #fbd38d", borderRadius:5, padding:"4px 8px",
                    marginTop:6, lineHeight:1.4 },
  paramCardHighlight: { border:"2px solid #4299e1", boxShadow:"0 0 0 3px rgba(66,153,225,0.25)" },
  helpLine:       { fontSize:12.5, color:"#718096", margin:"0 0 16px", lineHeight:1.5 },
  recap:          { background:"#f7fafc", border:"1px solid #e2e8f0", borderRadius:6,
                    padding:"10px 14px", marginBottom:12, fontSize:13, color:"#2d3748" },
  footer:         { borderTop:"1px solid #edf2f7", paddingTop:16, marginTop:4,
                    display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" },
  btn:            { padding:"10px 32px", background:"#2b6cb0", color:"#fff",
                    border:"none", borderRadius:6, fontSize:15, fontWeight:600,
                    cursor:"pointer", letterSpacing:"0.02em" },
  paramNameRow:   { display:"flex", alignItems:"center", gap:6, marginBottom:2 },
  badgeComposite: { fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:4,
                    textTransform:"capitalize" as const, background:"#ebf8ff",
                    color:"#2b6cb0", border:"1px solid #90cdf4" },
  badgeGrab:      { fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:4,
                    textTransform:"capitalize" as const, background:"#f0fff4",
                    color:"#276749", border:"1px solid #9ae6b4" },
  badgeSampleType:{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:4,
                    textTransform:"capitalize" as const, background:"#f7fafc",
                    color:"#718096", border:"1px solid #e2e8f0" },
  loadingRow:     { display:"flex", alignItems:"center", gap:6, marginTop:2,
                    paddingTop:4, borderTop:"1px solid #edf2f7" },
  loadingLabel:   { fontSize:11, color:"#718096" },
  loadingValue:   { fontSize:11, fontWeight:700, color:"#2b6cb0" },
};
